import React, { useState } from 'react';
import styled from 'styled-components';
import Adornment from './Adornment';

export interface AsureCloudTextInput extends React.InputHTMLAttributes<HTMLInputElement> {
  wrapperClassName?: string;
  endAdornmentElement?: React.ReactNode | string;
  helperText?: string;
  id: string;
  inputClassName?: string;
  labelDisplay: string;
  ref?: React.MutableRefObject<HTMLInputElement | null>;
  startAdornmentElement?: React.ReactNode | string;
}

/**
 * This component is custom built for Asure Cloud. Text inputs enable the user to interact with and input content and data.
 * It also includes the ability to add adornments to both before and after the input element.
 * Pass in custom HTML or ReactNode elements in either `startAdornmentElement` or `endAdornmentElement` to render custom elements used inside of the adornments.
 * For best results use [Font Awesome](https://fontawesome.com/) icons for the adornments.
 * For additional props, refer to [Input element on MDN Web Docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input) for input attributes that can be used with this component.
 * To import the component use the import statement: `import { AsureCloudTextInput } from '@asuresoftware/asure.design-system'`
 */

export const AsureCloudTextInput = ({
  wrapperClassName,
  endAdornmentElement,
  helperText,
  inputClassName,
  labelDisplay,
  ref,
  startAdornmentElement,
  ...props
}: AsureCloudTextInput) => {
  const [isInvalid, setInvalid] = useState<boolean>(false);

  const invalidClass = isInvalid && !props.disabled && !props.readOnly ? 'invalid' : '';
  const requiredClass = props.required ? 'required' : '';

  return (
    <TextInputWrapper className={`${wrapperClassName} input-wrapper`}>
      <label
        htmlFor={props.id}
        className={`d-block ${invalidClass} ${requiredClass}`}
      >
        {labelDisplay}
      </label>
      <div className="input-container d-flex">
        <input
          className={`${inputClassName} order-1 p-2`}
          ref={ref}
          required={props.required}
          onBlurCapture={(e) => e.target.checkValidity()}
          onInvalid={() => setInvalid(true)}
          onInput={() => isInvalid && setInvalid(false)}
          {...props}
        />
        {startAdornmentElement && (
          <Adornment
            className="order-0"
            customElement={startAdornmentElement}
            position="prefix"
          />
        )}
        {endAdornmentElement && (
          <Adornment
            className="order-2"
            customElement={endAdornmentElement}
            position="suffix"
          />
        )}
      </div>
      <small>{helperText}</small>
    </TextInputWrapper>
  );
};

export default AsureCloudTextInput;

const TextInputWrapper = styled.div`
  label {
    color: #434343;
    font-size: 0.875rem;
  }

  .required:after {
    content: '*';
    margin-left: 0.15rem;
    display: inline-block;
    color: #a20011;
  }

  .input-container {
    input {
      font-size: 0.875rem;
      border: 1px solid #ccc;
      color: black;
      outline: none;
      z-index: 1;

      &::placeholder {
        color: #bbbbbb;
      }

      &:focus {
        border-color: #006c93;
        box-shadow: 0px 0px 4px 2px rgba(0, 108, 147, 0.25);
      }

      &:disabled {
        color: #333333;
        -webkit-text-fill-color: #333333;
        background-color: #eee;
      }
      
      &:read-only {
        color: #333333;
        background-color: #eee;
      }
    }
  }

  .invalid ~ .input-container input {
    border-color: #a20011;
  }

  .invalid ~ .input-container input:focus {
    box-shadow: 0px 0px 4px 2px rgba(162, 0, 17, 0.25);
  }

  .invalid ~ .input-container span {
    color: #a20011;
    border-color: #a20011;
    background-color: #efdfde;
  }

  .invalid ~ .input-container svg {
    fill: #a20011;
  }

  small {
    color: #757575;
  }

  .invalid ~ small {
    color: #a20011;
  }
`;
